import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft } from "lucide-react";
import { useWallet } from "@/context/WalletContext";
import PinPad from "@/components/PinPad";
import { api } from "@/lib/api";

export default function ImportWallet() {
  const nav = useNavigate();
  const { commitWallet, validateMnemonic } = useWallet();
  const [step, setStep] = useState("seed"); // seed | pin | confirm
  const [phrase, setPhrase] = useState("");
  const [pin, setPin] = useState("");
  const [pinErr, setPinErr] = useState("");
  const cleaned = phrase.trim().toLowerCase().replace(/\s+/g, " ");
  const valid = validateMnemonic(cleaned);
  const words = cleaned ? cleaned.split(" ").length : 0;

  const finish = () => {
    const acc = commitWallet(cleaned, pin, words);
    // best-effort profile registration is skipped on import; user can add later
    api.post("/users", { name: "Imported User", email: `${acc.tron.address.slice(-8).toLowerCase()}@corewallet.local`, tron_address: acc.tron.address }).catch(() => {});
    toast.success("Wallet imported");
    nav("/portfolio", { replace: true });
  };

  const back = () => {
    if (step === "seed") nav("/onboarding");
    else if (step === "pin") setStep("seed");
    else { setStep("pin"); setPin(""); }
  };

  return (
    <div className="cw-shell min-h-screen px-6 py-6">
      <button onClick={back} className="mb-6 flex items-center gap-1 text-[#9CA3AF] hover:text-white">
        <ChevronLeft className="h-5 w-5" /> <span className="font-sans text-sm">Back</span>
      </button>

      {step === "seed" && (
        <div className="fade-up">
          <h1 className="font-outfit text-3xl font-extrabold">Import wallet</h1>
          <p className="mt-2 font-sans text-sm text-[#9CA3AF]">Enter your 12 or 24-word recovery phrase, separated by spaces.</p>
          <textarea
            data-testid="seed-phrase-textarea"
            value={phrase}
            onChange={(e) => setPhrase(e.target.value)}
            rows={5}
            placeholder="word1 word2 word3 ..."
            className="mt-6 w-full resize-none rounded-2xl border border-[#1F2430] bg-[#12141A] p-4 font-rajdhani text-lg text-white outline-none focus:border-neon/50"
          />
          <div className="mt-2 flex items-center justify-between font-sans text-xs">
            <span className="text-[#6B7280]">{words} words</span>
            {cleaned && (
              <span className={valid ? "text-[#10B981]" : "text-[#EF4444]"}>
                {valid ? "Valid phrase ✓" : "Invalid phrase"}
              </span>
            )}
          </div>
          <button
            data-testid="import-continue-button"
            disabled={!valid}
            onClick={() => setStep("pin")}
            className="mt-6 w-full rounded-2xl bg-neon py-4 font-outfit font-bold text-[#08090C] transition-opacity disabled:opacity-30 active:scale-[0.98] neon-glow"
          >
            Continue
          </button>
        </div>
      )}

      {step === "pin" && (
        <div className="fade-up flex flex-col items-center">
          <h1 className="font-outfit text-3xl font-extrabold">Set a PIN</h1>
          <p className="mb-10 mt-2 font-sans text-sm text-[#9CA3AF]">Encrypts your wallet on this device.</p>
          <PinPad onComplete={(p) => { setPin(p); setStep("confirm"); }} />
        </div>
      )}

      {step === "confirm" && (
        <div className="fade-up flex flex-col items-center">
          <h1 className="font-outfit text-3xl font-extrabold">Confirm PIN</h1>
          <p className="mb-10 mt-2 font-sans text-sm text-[#9CA3AF]">Re-enter your PIN.</p>
          <PinPad
            error={pinErr}
            onComplete={(p) => {
              if (p !== pin) { setPinErr("PINs don't match"); setStep("pin"); setPin(""); return; }
              finish();
            }}
          />
        </div>
      )}
    </div>
  );
}
